import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Layout,
  Typography, 
  Space, 
  Row, 
  Col, 
  Progress,
  Button,
  Card,
  Tag,
  Result,
  Modal
} from 'antd';
import { 
  CalendarOutlined,
  DatabaseOutlined,
  KeyOutlined,
  CopyOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ImportOutlined
} from '@ant-design/icons';
import { hapticFeedbackImpactOccurred, copyTextToClipboard } from '@telegram-apps/sdk-react';
import { useApp } from '../context/AppContext';
import ExtendSubscriptionModal from '../components/ExtendSubscriptionModal';

const { Content } = Layout;
const { Title, Text } = Typography;

const SubscriptionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { subscriptions, deleteSubscription, user, createPayment, t } = useApp();
  const [subscription, setSubscription] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);

  useEffect(() => {
    // Настраиваем BackButton при входе на страницу
    if (window.Telegram?.WebApp?.BackButton) {
      const backButton = window.Telegram.WebApp.BackButton;
      backButton.show();
      
      const handleBack = () => {
        navigate('/');
      };
      
      backButton.onClick(handleBack);
      
      return () => {
        backButton.offClick(handleBack);
        backButton.hide();
      };
    }
  }, [navigate]);

  useEffect(() => {
    // Ищем подписку по ID
    const foundSubscription = subscriptions.find(sub => sub.id === parseInt(id));
    setSubscription(foundSubscription);
  }, [id, subscriptions]);

  // Настройка MainButton для истекших подписок
  useEffect(() => {
    if (window.Telegram?.WebApp?.MainButton && subscription) {
      const mainButton = window.Telegram.WebApp.MainButton;
      
      // Показываем кнопку продления только для истекших подписок
      if (subscription.status === 'expired') {
        mainButton.setText(t('details_extend_btn'));
        mainButton.show();
        
        const handleExtendClick = () => {
          setIsExtendModalOpen(true);
        };

        mainButton.onClick(handleExtendClick);

        return () => {
          mainButton.offClick(handleExtendClick);
          mainButton.hide();
        };
      } else {
        mainButton.hide();
      }
    }
  }, [subscription, t]);

  const handleExtendSubscription = async (extendData) => {
    try {
      // Создаем запрос на продление через создание платежа
      const paymentData = {
        user_id: user.user_id,
        server_id: subscription.server_id,
        period: extendData.period,
        bonus_used: extendData.bonus_used,
        subscription_id: extendData.subscription_id
      };
      console.log('paymentData', paymentData);
      // Всегда создаем платеж - бэкенд сам решит как обрабатывать
      await createPayment(paymentData);
      
      setIsExtendModalOpen(false);
    } catch (error) {
      console.error('Ошибка продления подписки:', error);
    }
  };

  const handleCopyKey = async () => {
    const keyToCopy = subscription?.config_key || 'wg://config_key_example_123456789';
    
    try {
      await copyTextToClipboard(keyToCopy);
      
      // Показываем успешное копирование
      setIsCopied(true);
      hapticFeedbackImpactOccurred('light');
      
      // Сбрасываем состояние через 2 секунды
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      
    } catch (err) {
      console.error('Ошибка при копировании:', err);
      hapticFeedbackImpactOccurred('heavy');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('date_locale'), {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return {
          text: t('details_status_active'),
          color: 'success',
          icon: <CheckCircleOutlined />
        };
      case 'expiring_soon':
        return {
          text: t('details_status_expiring'),
          color: 'warning',
          icon: <ExclamationCircleOutlined />
        };
      case 'expired':
        return {
          text: t('details_status_expired'),
          color: 'error',
          icon: <ClockCircleOutlined />
        };
      case 'inactive':
        return {
          text: t('details_status_inactive'),
          color: 'default',
          icon: <ExclamationCircleOutlined />
        };
      default:
        return {
          text: t('details_status_unknown'),
          color: 'default',
          icon: <ClockCircleOutlined />
        };
    }
  };

  const getDaysRemaining = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTrafficUsedPercent = () => {
    if (!subscription || !subscription.traffic || subscription.traffic === 0) {
      return 0;
    }
    return Math.min(100, (subscription.used_traffic / subscription.traffic) * 100);
  };

  const formatTraffic = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} ${t('unit_gb')}`;
    } else if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} ${t('unit_mb')}`;
    } else {
      return `${(bytes / 1024).toFixed(1)} ${t('unit_kb')}`;
    }
  };

  const handleDeleteSubscription = () => {
    setIsModalOpen(true);
    hapticFeedbackImpactOccurred('light');
  };

  const handleConfirmDelete = async () => {
    if (subscription && user) {
      try {
        setIsDeleting(true);
        await deleteSubscription(subscription.id);
        setIsModalOpen(false);
        hapticFeedbackImpactOccurred('heavy');
        // Перенаправляем на главную страницу после удаления
        navigate('/');
      } catch (error) {
        console.error('Ошибка удаления подписки:', error);
        // Модальное окно останется открытым чтобы пользователь мог повторить попытку
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    hapticFeedbackImpactOccurred('light');
  };

  // Если подписки еще загружаются
  if (!subscriptions) {
    return (
      <Layout style={{ minHeight: '100vh', padding: '20px' }}>
        <Content>
          <Result
            icon={<DatabaseOutlined />}
            title={t('details_loading')}
            subTitle={t('details_wait')}
          />
        </Content>
      </Layout>
    );
  }

  if (!subscription) {
    return (
      <Layout>
        <Content style={{ padding: '16px', textAlign: 'center' }}>
          <Text>{t('details_not_found')}</Text>
        </Content>
      </Layout>
    );
  }

  const statusInfo = getStatusInfo(subscription.status);
  const daysRemaining = getDaysRemaining(subscription.expiry_date);
  const trafficUsedPercent = getTrafficUsedPercent();

  return (
    <Layout>
      <Content style={{ padding: '16px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          {/* Заголовок */}
          <Title level={3} style={{ margin: 0 }}>
            {t('details_title')}
          </Title>

          {/* Основная информация */}
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Row align="middle" gutter={16}>
                <Col flex="auto">
                  <Title level={3} style={{ margin: 0 }}>
                    {subscription.server_name}
                  </Title>
                </Col>
                <Col>
                  <Tag color={statusInfo.color}>
                    {statusInfo.text}
                  </Tag>
                </Col>
              </Row>
            </Space>
          </Card>

          {/* Блок с ключом конфигурации */}
          <Text strong type="primary" style={{ fontSize: '16px' }}>
            <KeyOutlined /> {t('details_config_key')}
          </Text>
          <Card 
            hoverable 
            onClick={handleCopyKey}
            type="secondary"
            style={{ 
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderColor: isCopied ? '#52c41a' : undefined
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  {t('details_click_to_copy')}
                </Text>
                <Text 
                  code 
                  style={{ 
                    fontSize: '13px',
                    wordBreak: 'break-all',
                    fontFamily: 'Monaco, Menlo, monospace',
                    display: 'block',
                    lineHeight: '1.4'
                  }}
                >
                  {subscription.config_key}
                </Text>
              </div>
              <div style={{ 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px'
              }}>
                {isCopied ? (
                  <CheckOutlined 
                    style={{ 
                      fontSize: '20px', 
                      color: '#52c41a',
                      transition: 'all 0.3s ease'
                    }} 
                  />
                ) : (
                  <CopyOutlined 
                    style={{ 
                      fontSize: '20px', 
                      color: 'var(--tg-theme-button-color, #1677ff)',
                      transition: 'all 0.3s ease'
                    }} 
                  />
                )}
              </div>
            </div>
            {isCopied && (
              <Text 
                style={{ 
                  fontSize: '12px',
                  marginTop: '8px',
                  display: 'block',
                  textAlign: 'center',
                  color: '#52c41a'
                }}
              >
                {t('details_copied')}
              </Text>
            )}
          </Card>
          
          <Text strong type="primary" style={{ fontSize: '16px' }}>
            <ImportOutlined /> {t('details_import')}
          </Text>
          <Row justify="space-evenly">
            <Button href={`v2raytun://import/${subscription.config_key}`} target="_blank" type="primary">
              v2RayTun
            </Button>
            <Button href={`hiddify://import/${subscription.config_key}#🌐 VPNCH1K - ${subscription.server_name} [sub_${subscription.id}]`} target="_blank" type="primary">
              Hiddify
            </Button>
          </Row>

          {/* Срок действия */}
          <Text strong type="primary" style={{ fontSize: '16px' }}>
            <CalendarOutlined /> {t('details_validity')}
          </Text>
          <Card>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '16px' }}>
                {t('details_valid_until')} {formatDate(subscription.expiry_date)}
              </Text>
              <Text type="secondary">
                {daysRemaining > 0 
                  ? t('details_days_left', { days: daysRemaining })
                  : t('details_expired')
                }
              </Text>
              {daysRemaining > 0 && (
                <Progress 
                  percent={Math.max(0, Math.min(100, (daysRemaining / 30) * 100))}
                  strokeColor={daysRemaining > 7 ? '#52c41a' : '#faad14'}
                  showInfo={false}
                />
              )}
            </Space>
          </Card>

          {/* Использование трафика */}
          <Text strong type="primary" style={{ fontSize: '16px' }}>
            <DatabaseOutlined /> {t('details_traffic')}
          </Text>
          <Card>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Row justify="space-between">
                <Text>{t('details_traffic_used')}</Text>
                <Text strong>{formatTraffic(subscription.used_traffic)} / {formatTraffic(subscription.traffic)}</Text>
              </Row>
              <Progress 
                percent={Math.round(trafficUsedPercent)}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            </Space>
          </Card>

          <Button type="primary" block danger style={{ minHeight: '45px' }} onClick={handleDeleteSubscription}>
            <strong>{t('details_delete_btn')}</strong>
          </Button>

          {/* Модальное окно подтверждения удаления */}
          <Modal
            title={t('configs_delete_title')}
            open={isModalOpen}
            onOk={handleConfirmDelete}
            onCancel={handleCancelDelete}
            footer={[
              <Button key="cancel" onClick={handleCancelDelete} disabled={isDeleting}>
                {t('cancel')}
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                onClick={handleConfirmDelete}
                loading={isDeleting}
              >
                {t('delete')}
              </Button>,
            ]}
          >
            <p>{t('configs_delete_confirm')}</p>
          </Modal>

          {/* Модальное окно продления подписки */}
          <ExtendSubscriptionModal
            visible={isExtendModalOpen}
            onCancel={() => setIsExtendModalOpen(false)}
            subscription={subscription}
            onExtend={handleExtendSubscription}
          />

        </Space>
      </Content>
    </Layout>
  );
};

export default SubscriptionDetailsPage;
